import { JobStatus } from '@coderule/qulite';
import type { SnapshotStatus } from '@coderule/clients';

import type { ServiceRunner } from '../service/ServiceLoops';
import type { BootstrapResult } from '../service/Bootstrap';
import type { HashState } from '../types';

const HASH_STATES: HashState[] = ['clean', 'dirty', 'hashing', 'missing'];

type StateCounts = Record<HashState, number>;

type QueueCounts = {
  pending: number;
  processing: number;
  done: number;
  failed: number;
};

export interface IndexingStatus {
  timestamp: number;
  root: {
    id: string;
    path: string;
  };
  files: {
    total: number;
    byState: StateCounts;
  };
  latestSnapshot: ReturnType<
    BootstrapResult['snapshotsRepo']['getLatest']
  > | null;
  serverStatus: SnapshotStatus | null;
  queue: QueueCounts;
  service: ReturnType<ServiceRunner['getServiceStateSnapshot']>;
}

export async function collectIndexingStatus(
  runtime: BootstrapResult,
  runner: ServiceRunner,
): Promise<IndexingStatus> {
  const byState = {} as StateCounts;
  for (const state of HASH_STATES) {
    byState[state] = runtime.filesRepo.countByState(state);
  }

  const total = HASH_STATES.reduce((acc, state) => acc + byState[state], 0);

  const queue = runtime.outbox.getQueue();
  const queueCounts: QueueCounts = {
    pending: queue.countByStatus(JobStatus.Pending),
    processing: queue.countByStatus(JobStatus.Processing),
    done: queue.countByStatus(JobStatus.Done),
    failed: queue.countByStatus(JobStatus.Failed),
  };

  const latestSnapshot = runtime.snapshotsRepo.getLatest() ?? null;
  let serverStatus: SnapshotStatus | null = null;

  if (latestSnapshot) {
    try {
      serverStatus = await runtime.clients.sync.checkSnapshotStatus(
        latestSnapshot.snapshot_hash,
      );
    } catch (error) {
      runtime.logger.warn(
        { err: error, snapshotHash: latestSnapshot.snapshot_hash },
        'Failed to fetch server snapshot status',
      );
    }
  }

  return {
    timestamp: Date.now(),
    root: {
      id: runtime.config.rootId,
      path: runtime.config.rootPath,
    },
    files: {
      total,
      byState,
    },
    latestSnapshot,
    serverStatus,
    queue: queueCounts,
    service: runner.getServiceStateSnapshot(),
  };
}

export function formatStatus(status: IndexingStatus): string {
  const lines: string[] = [];

  lines.push('=== Coderule Indexing Status ===');
  lines.push('');

  lines.push(`Timestamp: ${new Date(status.timestamp).toISOString()}`);
  lines.push('');

  lines.push('Repository:');
  lines.push(`  ID: ${status.root.id}`);
  lines.push(`  Path: ${status.root.path}`);
  lines.push('');

  lines.push('Files:');
  lines.push(`  Total: ${status.files.total}`);
  lines.push('  States:');
  lines.push(`    Clean: ${status.files.byState.clean}`);
  lines.push(`    Dirty: ${status.files.byState.dirty}`);
  lines.push(`    Hashing: ${status.files.byState.hashing}`);
  lines.push(`    Missing: ${status.files.byState.missing}`);
  lines.push('');

  lines.push('Queue:');
  lines.push(`  Pending: ${status.queue.pending}`);
  lines.push(`  Processing: ${status.queue.processing}`);
  lines.push(`  Done: ${status.queue.done}`);
  lines.push(`  Failed: ${status.queue.failed}`);
  lines.push('');

  if (status.latestSnapshot) {
    lines.push('Latest Snapshot:');
    lines.push(`  Hash: ${status.latestSnapshot.snapshot_hash}`);
    lines.push(`  Files: ${status.latestSnapshot.files_count}`);
    lines.push(`  Size: ${formatBytes(status.latestSnapshot.total_size)}`);
    lines.push(
      `  Created: ${new Date(status.latestSnapshot.created_at).toISOString()}`,
    );

    if (status.serverStatus) {
      lines.push(`  Server Status: ${status.serverStatus.status}`);

      if (
        status.serverStatus.status === 'READY' &&
        status.serverStatus.timing
      ) {
        lines.push('');
        lines.push('  Indexing Performance:');
        const timing = status.serverStatus.timing;

        if (timing.pass1 !== undefined) {
          lines.push(
            `    Pass 1 (File Discovery): ${formatDuration(timing.pass1)}`,
          );
        }
        if (timing.gap !== undefined) {
          lines.push(`    Gap (Waiting): ${formatDuration(timing.gap)}`);
        }
        if (timing.pass2 !== undefined) {
          lines.push(
            `    Pass 2 (Cross-Reference): ${formatDuration(timing.pass2)}`,
          );
        }
        if (timing.pass2_hdf5_compile !== undefined) {
          lines.push(
            `    HDF5 Compilation: ${formatDuration(timing.pass2_hdf5_compile)}`,
          );
        }
        if (timing.pass2_hdf5_dedup !== undefined) {
          lines.push(
            `    HDF5 Deduplication: ${formatDuration(timing.pass2_hdf5_dedup)}`,
          );
        }
        if (timing.pass2_hdf5_write !== undefined) {
          lines.push(
            `    HDF5 Write I/O: ${formatDuration(timing.pass2_hdf5_write)}`,
          );
        }

        const totalTime =
          (timing.pass1 ?? 0) + (timing.gap ?? 0) + (timing.pass2 ?? 0);
        if (totalTime > 0) {
          lines.push(`    Total Time: ${formatDuration(totalTime)}`);
        }

        lines.push('');
        lines.push('  Indexing Summary:');
        if (timing.total_files !== undefined) {
          lines.push(
            `    Files Indexed: ${timing.total_files.toLocaleString()}`,
          );
        }
        if (timing.total_chunks !== undefined) {
          lines.push(
            `    Chunks Created: ${timing.total_chunks.toLocaleString()}`,
          );
        }
        if (timing.total_classes !== undefined) {
          lines.push(
            `    Classes Found: ${timing.total_classes.toLocaleString()}`,
          );
        }
        if (timing.total_methods !== undefined) {
          lines.push(
            `    Methods Found: ${timing.total_methods.toLocaleString()}`,
          );
        }
      } else if (status.serverStatus.message) {
        lines.push(`  Message: ${status.serverStatus.message}`);
      }
    }
  } else {
    lines.push('Latest Snapshot: None');
  }
  lines.push('');

  lines.push('Service State:');
  lines.push(
    `  Last Change: ${new Date(status.service.lastChangeAt).toISOString()}`,
  );
  lines.push(
    `  Last Snapshot Ready: ${new Date(status.service.lastSnapshotReadyAt).toISOString()}`,
  );
  lines.push(
    `  Last Heartbeat: ${status.service.lastHeartbeatEnqueuedAt > 0 ? new Date(status.service.lastHeartbeatEnqueuedAt).toISOString() : 'Never'}`,
  );
  lines.push(`  Watcher Ready: ${status.service.watcherReady ? 'Yes' : 'No'}`);
  lines.push(`  Buffering: ${status.service.buffering ? 'Yes' : 'No'}`);

  return lines.join('\n');
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${units[i]}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 0.001) return '<1ms';
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  if (seconds < 60) return `${seconds.toFixed(2)}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m ${remainingSeconds.toFixed(0)}s`;
}
